/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import components from './components';
import {
  CustomKeyboardEvent,
  CustomMouseEvent,
  Events,
  UserKeyEvent,
  UserMouseEvent
} from './Events';
import Emitter from '@exc/emitter';
import {ExternalConfiguration} from './ExternalConfiguration';
import {gettify, isComponent, isShell} from './util';
import helpCenter from './helpcenter';
import {markEventsPerformance} from '@exc/shared';
import Messenger, {messenger} from './Messenger';
import metrics from './metrics';
import Metrics, {Level} from '@exc/metrics';
import MetricsRuntime from '@exc/metrics-runtime';
import network from './network';
import nps from './nps';
import {
  onClick,
  onContextMenu,
  onKeyDown,
  onMouseDown
} from './actions';
import orgSwitcher from './orgswitcher';
import page, {addTakeover, onConfigure as onPageConfigure} from './page';
import proxiedConfig, {ConfigProxy, setupConfig} from './config';
import pulse from './pulse';
import {Runtime as RuntimeInterface} from '@adobe/exc-app/index';
import SessionApi from './api/SessionApi';
import {settings} from './settings';
import shell from './api/ShellApi';
import sidebar from './sidebar';
import topbar from './topbar';
import user from './user';
import userProfile from './userprofile';
import {watchHash, watchHistory, watchLocation} from './history';

export interface InitialConfiguration {
  canTakeover?: boolean;
}

export interface MenuData {
  config: {
    subject: string;
    type: 'CONTEXTUAL_FEEDBACK_SUBMISSION';
  };
  selectedTab: 'feedback';
}

interface Callback {(value?: any): void}

// Only initialize metrics in the case of being in an iframe
if ((window as any).parent !== (window as any) || process.env.NODE_ENV === 'test') {
  MetricsRuntime.configure();
}

export const RUNTIME_MARKED_EVENTS = [
  'Runtime.constructed',
  'Runtime.ready',
  'Runtime.done',
  'Messenger.connected'
];

const URL_WATCHERS = {
  HASH: watchHash,
  HISTORY: watchHistory,
  SERVER: watchLocation
};

const app = () => import('./api/AppApi').then(api => new api.default());

export class Runtime extends Emitter implements RuntimeInterface {
  public app = app;
  public configured = false;
  public messenger: Messenger = messenger;
  public pulse = pulse();
  public session: SessionApi;
  public settings = settings();
  public shell = shell();
  public user = user();

  // Configuration proxy -- see proxiedConfig for accessors
  public customEnvLabel: ConfigProxy['customEnvLabel'];
  public customButtons: ConfigProxy['customButtons'];
  public customSearch: ConfigProxy['customSearch'];
  public digitalData: ConfigProxy['digitalData'];
  public feedback: ConfigProxy['feedback'];
  public helpCenter: ConfigProxy['helpCenter'];
  public logoutUrl: ConfigProxy['logoutUrl'];
  public nps: ConfigProxy['nps'];
  public sidebar: ConfigProxy['sidebar'];
  public solution: ConfigProxy['solution'];
  public subOrgs: ConfigProxy['subOrgs'];
  public workspaces: ConfigProxy['workspaces'];

  private _legacyCanTakeover: boolean;
  private _warnedConfigs: Set<string>;

  private metrics: Metrics;
  private onCustomProfileClick!: Callback;
  private skipMenuList?: {
    [index: string]: HTMLElement;
  };

  constructor(configuration?: InitialConfiguration) {
    super();

    proxiedConfig.clear();

    configuration = configuration || {};

    this._legacyCanTakeover = 'canTakeover' in configuration;
    this._warnedConfigs = new Set();
    this.metrics = new Metrics('exc.module-runtime');

    // Adds a Metrics filter to create performance marks for events listed in RUNTIME_MARKED_EVENTS
    markEventsPerformance(
      (window as any).adobeMetrics,
      RUNTIME_MARKED_EVENTS,
      event => 'rt_' + event.toLowerCase().replace(/[ ().\-&]+/g, '_')
    );

    this.metrics.event('Runtime.constructed');
    this.messenger.on('message', this.receive);

    let {href, pathname, hash, search} = window.location;
    isComponent() || this.send(Events.READY, {configuration, hash, href, pathname, search});

    // API's
    this.session = new SessionApi(this.messenger);

    setupConfig(this, [
      'appContainer',
      'customEnvLabel',
      'customButtons',
      'customSearch',
      'digitalData',
      'favicon',
      'feedback',
      'helpCenter',
      'logoutUrl',
      'modal',
      'nps',
      'preventDefaultCombos',
      'sidebar',
      'solution',
      'spinner',
      'subOrgs',
      'title',
      'unloadPromptMessage',
      'viewportTakeover',
      'workspaces'
    ], (apiName, value) => {
      if (value && (apiName === 'viewportTakeover' || apiName === 'appContainer')) {
        addTakeover();
      }
    });

    // Add the viewport takeover DOM element to the page if enabled in the
    // config.
    configuration.canTakeover && addTakeover();

    this.onPageChange();

    if (this._legacyCanTakeover) {
      this.deprecationWarnings(configuration);
    }
  }

  public set config(config: ConfigProxy) {
    if (config['viewportTakeover'] || config['appContainer']) {
      addTakeover();
    }
    proxiedConfig.set(config);
  }

  public get config(): ConfigProxy {
    return proxiedConfig.config();
  }

  /**
   * Stores a customHeroClick function for later use
   * and sends a post message to the Sandbox that a customHeroClick
   * exists.
   * @param fn Callback function for custom behavior from solution.
   */
  public set heroClick(fn: Callback) {
    topbar.onHeroClick(fn);
  }

  /**
   * Stores a profileClick function which is
   * invoked when a user clicks and custom profile buttons in the User Profile
   * view in Shell.
   * @param fn Callback function for custom behavior from solution.
   */
  public set customProfileClick(fn: Callback) {
    this.onCustomProfileClick = fn;
  }

  /**
   * Method called from solution to open link in a new tab.
   * @param path Relative path.
   */
  public openInNewTab = page.openInNewTab;

  /**
   * Redirects to another unified shell solution.
   * @param path Path including search and hash to a unified shell solution
   * url.
   */
  public shellRedirect = page.shellRedirect;

  /**
   * Allows a solution to open a Shell menu by sending a
   * post message to the Sandbox.
   * @param menuKey A string to identify which shell menu to open.
   * Acceptable values include 'helpCenter'.
   * @param menuData An object data the menu will need.
   */
  public openMenu(menuKey: string, menuData: MenuData): void {
    switch (menuKey) {
      case 'helpCenter':
        helpCenter.open(menuData);
        break;
      default:
        this.metrics.warn(`Runtime: unknown openMenu key ${menuKey}`);
    }
  }

  /**
   * Allows the a11y skip menu to set focus on an element inside the iframe
   * @param dataKey A string to identify which element to focus
   */
  private setKeyboardFocus(dataKey: string): void {
    const node = (this.skipMenuList || {})[dataKey];
    if (node) {
      // Right now "main" is not part of the sequential keyboard
      // navigation, so -1 is the value we want in the "main" case
      // as we programmatically move focus while not adding "main"
      // to the sequential order
      node.tabIndex = -1;
      node.focus();
    }
  }

  /**
   * Add style element to the DOM that sets up the shell height
   * necessary for fullscreen takeover functionality.
   * @param shellHeight Height of the Shell UI.
   */
  private addPlaceholderStyle({shellHeight}: {shellHeight: number}): void {
    let styl = document.getElementById('shell-placeholder-style');
    if (!shellHeight) {
      return;
    }
    // The style element exists already don't to add a style element.
    if (styl) {
      styl.innerHTML = `#shell-placeholder {height: ${shellHeight}px;}`;
      return;
    }
    styl = document.createElement('style');
    styl.id = 'shell-placeholder-style';
    styl.innerHTML = `#shell-placeholder {height: ${shellHeight}px;}`;
    document.head.append(styl);
  }

  private addSideNavStyles({appContainer, shellHeight, shellSideNavPresent, shellSideNavWidth}: {
    appContainer: string; shellHeight: number; shellSideNavPresent: boolean; shellSideNavWidth: number;
  }): void {
    if (!appContainer) {
      return;
    }
    let sideStyl = document.getElementById('shell-side-style');
    if (!sideStyl) {
      sideStyl = document.createElement('style');
      sideStyl.id = 'shell-side-style';
    }
    if (!shellSideNavPresent || !shellSideNavWidth) {
      // When user changes side nav visibility with hamburger, style should reset
      sideStyl.innerHTML = '';
      return;
    }
    sideStyl.innerHTML = `
      body {
        overflow: hidden;
      }

      body ${appContainer} {
        margin-left: ${shellSideNavWidth}px;
        overflow: scroll;
        height: calc(100vh - ${shellHeight}px);
      }
    `;
    document.head.append(sideStyl);
  }

  /**
   * Scrubs the DOM inside the iframe for a11y skip menu elements.
   * Currently finds the main landmark (if it exists), stores the list, and
   * sends a post message to the Sandbox with the skip menu options.
   */
  private getSkipMenuList(): void {
    let skipMenuList = null;
    // Always include main landmark
    let main = document.querySelector('main, [role="main"]');
    if (main) {
      this.skipMenuList = {mainLandmark: main as HTMLElement};
      skipMenuList = [{
        id: 'mainLandmark',
        label: 'Skip to main content'
      }];
    }
    this.send(Events.SKIP_MENU_SET_LIST, {skipMenuList});
  }

  private receive = (event: MessageEvent): void => {
    let data = event.data || {};

    let type: string = data.type;
    let value: any = data.value;

    switch (type) {
      case Events.CONFIGURE:
        this.addPlaceholderStyle(value);
        this.addSideNavStyles(value);
        this.onConfigure(value);
        break;
      case Events.CUSTOM_FEEDBACK:
        this.metrics.info('Runtime.onUseCustomFeedback');
        this.emit('customFeedback', {});
        break;
      case Events.CUSTOM_PROFILE_CLICK:
        this.onCustomProfileClick(value);
        break;
      case Events.CUSTOM_SEARCH:
        this.emit('customSearch', value);
        break;
      case Events.CUSTOM_PROFILE_BUTTON_CLICK:
      case Events.CUSTOM_HELP_BUTTON_CLICK:
        this.emit('customButtonClick', data);
        break;
      case Events.HISTORY:
        this.onExternalHistoryChange(value);
        break;
      case Events.NPS:
        this.metrics.analytics.trackEvent({
          action: 'submit',
          element: value,
          feature: 'nps',
          type: 'nps',
          widget: {name: 'nps', type: 'form'}
        });
        break;
      case Events.SKIP_MENU_FOCUS:
        this.setKeyboardFocus(value);
        break;
      case Events.SKIP_MENU_GET_LIST:
        this.getSkipMenuList();
        break;
      case Events.USER_INPUT:
        this.onShellUserInput(value);
        break;
    }
  };

  /**
   * Called when the channel communication is established and the
   * config is fetched; generally application will bootstrap and start
   * rendering after this callback is invoked.
   * @param config Object with all configurations.
   */
  public onConfigure = (config: ExternalConfiguration): void => {
    MetricsRuntime.configure({
      ...config.metricsConfig,
      environment: config.metricsEnv || config.environment
    });
    (window as any).adobeMetrics.setApplication({
      id: config.metricsAppId,
      version: (window as any).config && (window as any).config.spaVersion
    });
    this.metrics.event(`Runtime.${this.configured ? 'configuration' : 'ready'}`, config);
    (window as any).shellGainsightEnabled = config.gainsightEnabled;

    //configure and update network api with latest config
    network.configure(Object.assign({}, config, {
      getTokenInfo: () => Promise.resolve({token: config.imsToken})
    }));

    config.shellInfo && gettify(config.shellInfo, 'shellInfo');
    this.emit(this.configured ? 'configuration' : 'ready', config);
    this.updateDigitalData(config);

    onPageConfigure(config);
    // Only continue on the initial receipt of the `configure` event
    if (this.configured) {
      return;
    }

    config.analytics && MetricsRuntime.configure({
      analytics: {
        ...config.analytics,
        version: (window as any).config && (window as any).config.spaVersion
      }
    });

    const allowedHosts = config.hosts || [window.location.host];
    window.addEventListener('click', e => onClick(e, config, allowedHosts, messenger));
    window.addEventListener('keydown', e => onKeyDown(e, messenger));
    window.addEventListener('contextmenu', e => onContextMenu(e, config, allowedHosts));
    window.addEventListener('mousedown', e => onMouseDown(e, config, allowedHosts));

    this.configured = true;
    URL_WATCHERS[config.historyType](this.onInternalHistoryChange);

    this.deprecationWarnings(config);
  };

  public onExternalHistoryChange = (path: any): void => {
    this.metrics.event('Runtime.onExternalHistoryChange', path);
    this.emit('history', {path, type: 'external'});
  };

  public onInternalHistoryChange = (state: Record<string, any>, title: string, path?: string): void => {
    this.metrics.event('Runtime.onLocationChange', {path, state, title});
    this.send(Events.HISTORY, {href: window.location.href, path, state, title});
    this.emit('history', {path, type: 'internal'});
    this.onPageChange();
  };

  public generateShellUrl = page.generateShellUrl;

  public blockNavigation = page.blockNavigation;

  /**
   * Checks for the window.digitalData object required for Target
   * Recommendations service and stores the value to check for changes.
   */
  public onPageChange(): void {
    let win = window as any;
    let digitalDataTimeout: any;

    let check = (attempt: number): void => {
      if (attempt > 5) {
        return;
      }

      let dd = win.digitalData;
      if (!dd) {
        digitalDataTimeout = setTimeout(check.bind(this, attempt + 1), 1000);
        return;
      }

      this.digitalData = JSON.stringify(dd);
      this.emit('digitalData');
    };

    clearTimeout(digitalDataTimeout);
    check(1);
  }

  /**
   * This function prepares the events to be sent to the iframe.
   * @param e The event from the outer shell.
   */
  public onShellUserInput(e: UserKeyEvent | UserMouseEvent): void {
    this.metrics.event('Runtime.onShellUserInput', e);

    let triggerEvent: CustomKeyboardEvent | CustomMouseEvent | null = null;
    switch (e.type) {
      case 'keyup':
      case 'keydown':
        triggerEvent = new KeyboardEvent(e.type, {...e, bubbles: true});
        break;
      case 'click':
      case 'mouseup': {
        triggerEvent = new MouseEvent(e.type, {...e, bubbles: true});
        break;
      }
    }
    if (triggerEvent) {
      triggerEvent.data = (e as UserKeyEvent | UserMouseEvent).data;
      triggerEvent.fromShell = true;
      document.body.dispatchEvent(triggerEvent);
    }
  }

  public send(type: string, data?: any): void {
    this.messenger.send(type, data);
  }

  private updateDigitalData(config: ExternalConfiguration): void {
    if ((window as any).digitalData) {
      // Block `shellInfo` from being sent here because it's contents are all
      // getters, which all fire when metrics sends them.
      const obj = Object.assign({}, config, {shellInfo: {}});
      (window as any).digitalData.unifiedShell = {config: obj};
      return;
    }
    this.on('digitalData', this.updateDigitalData.bind(this, config));
  }

  /**
   * Called when user needs to reload iframe URL.
   */
  public iframeReload = page.iframeReload;

  /**
   * Called when user's auth expires or times out and user has to
   * log in again.
   */
  public authExpired = user().authExpired;

  /**
   * Launch 404 page from anywhere
   */
  public notFound = page.notFound;

  /**
   * Called to warn and log when any deprecated configs are used.
   */
  private deprecationWarnings(config: Record<string, any>): void {
    let configs: string[] = [];
    this._legacyCanTakeover && configs.push('canTakeover');
    config.viewportTakeover && configs.push('viewportTakeover');

    configs.forEach(key => {
      // Don't warn again if already warned before
      if (!this._warnedConfigs.has(key)) {
        this.metrics.event('Runtime.deprecationWarnings', key, {level: Level.WARN});
        if (config.environment !== 'prod') {
          // eslint-disable-next-line no-console
          console.warn(`This method of configuring ${key} is deprecated. Please use the up-to-date method.`);
        }
        this._warnedConfigs.add(key);
      }
    });
  }

  public done = page.done;
}

export {
  app,
  components,
  helpCenter,
  metrics,
  network,
  nps,
  orgSwitcher,
  page,
  pulse,
  settings,
  shell,
  sidebar,
  topbar,
  user,
  userProfile
};

let instance: Runtime | undefined;
const INIT_TIMEOUT = 5000;
const MAX_ALERTS = 10;

export function waitForInit(): void {
  let counter = 0;
  let sandboxInformed = false;
  const metrics = new Metrics('exc.module-runtime');
  let instanceCheck = setInterval(() => {
    if (!instance) {
      sandboxInformed || messenger.send(Events.NOT_INITIALIZED);
      if (counter === MAX_ALERTS) {
        clearInterval(instanceCheck);
        metrics.warn(`Runtime not instantiated after ${INIT_TIMEOUT * MAX_ALERTS}ms.`);
        return;
      }
      metrics.warn('Runtime not instantiated yet.');
      counter++;
      sandboxInformed = true;
      return;
    }
    clearInterval(instanceCheck);
  }, INIT_TIMEOUT);
}

isShell() || waitForInit();

export function clearInstance(): void {
  instance = undefined;
}

(window as any)['exc-module-runtime'] = Object.assign(module.exports, {
  default: function(configuration?: InitialConfiguration): Runtime {
    if (instance) {
      return instance;
    }
    instance = new Runtime(configuration);
    return instance;
  }
});
